import { Subscription } from '@eagle/core-data-types';
import { Alert, Button, Card, CardContent, Stack, Table, TableContainer, Typography } from '@ethings/ui-toolkit';
import Axios from 'axios';
import { FC, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router';
import { useAuthenticated } from '../../auth';
import { FlexBox, Icon, SubscriptionListDesktopView, SubscriptionListMobileView, useBoolFlag } from '../../components';
import { GeneralPaginatedList, GeneralPaginatedListView } from '../../components/general-paginated-list';
import { T_MANY, T_ONE } from '../../constants';
import { useCustomRoutes, useResponsiveText } from '../../hooks';
import { CacheDataTypes } from '../../types';
import { FILTER_OUT, innerText } from '../../util';
import { FindItemsDeferredResult, Query } from '../list';

export const PROFILE_PAGE_CREATE_SUBSCRIPTIONS_FLAG = 'portals-profile-enable-create-subscription';

export const ProfilePageSubscriptions: FC = () => {
  const { restClient } = useAuthenticated();
  const { t } = useTranslation(['common']);
  const { subscription: customRouteSubscription } = useCustomRoutes();
  const itemType = t('common:terms.subscription', { count: T_MANY });
  const createSubscriptionHref = useHref(`/profile/${customRouteSubscription}/create`);
  const createSubscriptionPermissions = useBoolFlag(PROFILE_PAGE_CREATE_SUBSCRIPTIONS_FLAG);
  const subscriptionDetailPath = `/profile/${customRouteSubscription}`;

  const titleText = t('common:terms.subscription', { count: T_MANY });
  const h5Styles = useResponsiveText('1.5rem', innerText(titleText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const findSubscriptions = useCallback(({ pagination }: Query): FindItemsDeferredResult<Subscription> => {
    const cancelToken = Axios.CancelToken.source();

    return {
      cancel: cancelToken.cancel,
      promise: restClient.my.subscription.getAllV2({
        ...pagination,
        filter: FILTER_OUT.finish,
        sort: JSON.stringify({ start: { when: 'desc' } }),
      }, { cancelToken: cancelToken.token }).then((response) => {
        return {
          result: {
            results: response.items,
            itemCount: response.count ?? 0,
          },
          resultDescription: '',
        };
      }),
    };
  }, [restClient]);

  const renderCreateSubscriptionsButton = (): JSX.Element => {
    if (!createSubscriptionPermissions) return <></>;
    return (
      (() => {
        return (
          <Button
            href={createSubscriptionHref}
            startIcon={<Icon icon="AlertIcon" />}
          >
            {t('common:common.action.create-entity', { entity: t('common:terms.subscription', { count: T_ONE }) })}
          </Button>
        );
      })()
    );
  };

  const titleAndAction = (
    <FlexBox justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
      <Typography
        variant="h5"
        style={{
          fontSize: h5Styles.fontSize,
          lineHeight: h5Styles.lineHeight,
          wordBreak: h5Styles.wordBreak,
          whiteSpace: h5Styles.whiteSpace,
          overflow: h5Styles.overflow,
          textOverflow: h5Styles.textOverflow,
        }}
      >
        {titleText}
      </Typography>
      {renderCreateSubscriptionsButton()}
    </FlexBox>
  );

  const emptyState = <Alert severity='info'>{t('common:common.hint.list.none', { entity: itemType })}</Alert>;

  return (
    <GeneralPaginatedList<Subscription>
      limit={5}
      onQueryChanged={findSubscriptions}
      renderContent={({ isLoading, items, matchCount, pagination, setPagination, hasError }) =>
        <GeneralPaginatedListView<Subscription>
          hasError={hasError}
          isLoading={isLoading}
          items={items}
          matchCount={matchCount}
          pagination={pagination}
          renderDesktopContent={({ paginationElement, items: subscriptions }) => (
            <Stack spacing={2}>
              <Card>
                <CardContent>
                  {titleAndAction}
                </CardContent>
                {subscriptions.length > 0
                  ? <TableContainer>
                    <Table component="div">
                      <SubscriptionListDesktopView
                        subscriptions={subscriptions}
                        subscriptionDetailPath={subscriptionDetailPath}
                        userRecipientDataType={CacheDataTypes.MY_USER}
                        isLoading={isLoading}
                      />
                    </Table>
                  </TableContainer>
                  : <CardContent>
                    {emptyState}
                  </CardContent>
                }
                {paginationElement}
              </Card >
            </Stack >
          )}
          renderMobileContent={({ paginationElement, items: subscriptions }) => (
            <Stack spacing={2}>
              {titleAndAction}
              {subscriptions.length > 0
                ? <SubscriptionListMobileView
                  subscriptions={subscriptions}
                  subscriptionDetailPath={subscriptionDetailPath}
                  userRecipientDataType={CacheDataTypes.MY_USER}
                  isLoading={isLoading}
                  displayOnCard
                />
                : emptyState
              }
              {paginationElement}
            </Stack >
          )}
          setPagination={setPagination}
        />
      }
    />
  );
};
